<template>
  <v-container fluid class="pb-8">
    <v-layout align-start>
      <v-flex>
        <v-card flat>
          <v-row no-gutters justify="center">
            <span style="font-size: 34px; color: #616161">
              <v-icon large class="pr-1 pb-2">mdi-chart-timeline-variant</v-icon>
              Reportes de compras
            </span>
          </v-row>

          <v-card-text class="px-3 px-sm-6 px-md-8 pt-2 pb-0">
            <ReportFilters class="pt-3" :loading="loading" @applyFilters="onFiltersChanged" />

            <v-row justify="end" no-gutters class="mt-1">
              <v-btn
                color="success"
                outlined
                small
                :loading="exporting"
                :disabled="loading || !currentFilters"
                @click="exportExcel"
              >
                <v-icon left small>mdi-file-excel</v-icon>
                Exportar Excel
              </v-btn>
            </v-row>

            <!-- Resumen -->
            <v-row class="mt-1" dense>
              <v-col v-for="card in summaryCards" :key="card.label" cols="6" sm="4" lg="2">
                <v-card outlined class="metric-card pa-3 fill-height">
                  <div class="d-flex align-center justify-space-between">
                    <div class="metric-label">{{ card.label }}</div>
                    <v-avatar :color="card.bg" size="30">
                      <v-icon small :color="card.color">{{ card.icon }}</v-icon>
                    </v-avatar>
                  </div>
                  <div class="metric-value mt-1">{{ card.value }}</div>
                  <div class="metric-sub">{{ card.sub }}</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Tiempo entre dos estatus (acumulado) -->
            <v-card outlined class="mt-3">
              <v-card-title class="py-2">
                <span class="section-title">Tiempo entre dos estatus</span>
              </v-card-title>
              <v-card-text>
                <v-row align="center" dense>
                  <v-col cols="12" sm="5" md="4">
                    <v-autocomplete
                      v-model="pathFrom"
                      :items="statusOptions"
                      item-text="name"
                      item-value="id"
                      label="Desde"
                      prepend-inner-icon="flag"
                      color="cyanText"
                      outlined
                      dense
                      hide-details
                      @change="getPath"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="2" md="1" class="text-center py-0">
                    <v-icon color="grey">east</v-icon>
                  </v-col>
                  <v-col cols="12" sm="5" md="4">
                    <v-autocomplete
                      v-model="pathTo"
                      :items="statusOptions"
                      item-text="name"
                      item-value="id"
                      label="Hasta"
                      prepend-inner-icon="sports_score"
                      color="cyanText"
                      outlined
                      dense
                      hide-details
                      @change="getPath"
                    ></v-autocomplete>
                  </v-col>
                </v-row>

                <div v-if="pathLoading" class="text-center py-6">
                  <v-progress-circular indeterminate color="primary" />
                </div>
                <div v-else-if="pathFrom === pathTo" class="orange--text text--darken-2 py-4">
                  Selecciona dos estatus distintos.
                </div>
                <div v-else-if="!pathResult || !pathResult.orders" class="grey--text py-4">
                  Ninguna orden pasó de "{{ statusName(pathFrom) }}" a "{{ statusName(pathTo) }}" en el período seleccionado.
                </div>
                <template v-else>
                  <div class="path-summary mt-3 mb-2">
                    En promedio, una orden tarda
                    <strong>{{ humanize(pathResult.avg_hours) }}</strong>
                    en pasar de "<strong>{{ statusName(pathFrom) }}</strong>" a
                    "<strong>{{ statusName(pathTo) }}</strong>"
                    <span class="grey--text">({{ formatInt(pathResult.orders) }} órdenes)</span>.
                  </div>
                  <v-row dense>
                    <v-col v-for="card in pathCards" :key="card.label" cols="6" md="3">
                      <v-card outlined class="metric-card pa-3 fill-height">
                        <div class="d-flex align-center justify-space-between">
                          <div class="metric-label">{{ card.label }}</div>
                          <v-avatar :color="card.bg" size="30">
                            <v-icon small :color="card.color">{{ card.icon }}</v-icon>
                          </v-avatar>
                        </div>
                        <div class="metric-value mt-1">{{ card.value }}</div>
                      </v-card>
                    </v-col>
                  </v-row>
                </template>
              </v-card-text>
            </v-card>

            <!-- Gráfico transiciones -->
            <v-card outlined class="mt-3">
              <v-card-title class="py-2 flex-wrap">
                <span class="section-title">Tiempo promedio por transición de estatus</span>
                <v-spacer />
                <v-btn-toggle v-model="orderBy" dense mandatory color="primary">
                  <v-btn small value="avg_hours">Por duración</v-btn>
                  <v-btn small value="transitions">Por frecuencia</v-btn>
                </v-btn-toggle>
              </v-card-title>
              <v-card-text>
                <div v-if="loading" class="text-center py-10">
                  <v-progress-circular indeterminate color="primary" />
                </div>
                <div v-else-if="!visibleTransitions.length" class="text-center grey--text py-10">
                  No se encontraron transiciones con los filtros aplicados.
                </div>
                <apexchart
                  v-else
                  ref="transitionChart"
                  type="bar"
                  :height="chartHeight(transitionChartData.length)"
                  :options="transitionChartOptions"
                  :series="transitionChartSeries"
                />
              </v-card-text>
            </v-card>

            <!-- Tiempo promedio por aprobador -->
            <v-card outlined class="mt-3">
              <v-card-title class="py-2">
                <span class="section-title">Tiempo promedio por aprobador</span>
                <v-spacer />
                <v-text-field
                  v-model="approverSearch"
                  append-icon="search"
                  label="Buscar aprobador"
                  single-line
                  hide-details
                  outlined
                  dense
                  style="max-width: 320px"
                ></v-text-field>
              </v-card-title>
              <v-card-text>
                <div v-if="loading" class="text-center py-10">
                  <v-progress-circular indeterminate color="primary" />
                </div>
                <div v-else-if="!approvers.length" class="text-center grey--text py-10">
                  No hay aprobaciones en el período seleccionado.
                </div>
                <template v-else>
                  <apexchart
                    ref="approverChart"
                    type="bar"
                    :height="chartHeight(approverChartData.length)"
                    :options="approverChartOptions"
                    :series="approverChartSeries"
                  />
                  <v-data-table
                    :headers="approverHeaders"
                    :items="approvers"
                    :search="approverSearch"
                    locale="es-do"
                    :items-per-page="10"
                    :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
                    class="mt-2"
                  >
                    <template v-slot:[`item.approvals`]="{ item }">{{ formatInt(item.approvals) }}</template>
                    <template v-slot:[`item.avg_hours`]="{ item }"><strong>{{ humanize(item.avg_hours) }}</strong></template>
                    <template v-slot:[`item.median_hours`]="{ item }">{{ humanize(item.median_hours) }}</template>
                    <template v-slot:[`item.min_hours`]="{ item }">{{ humanize(item.min_hours) }}</template>
                    <template v-slot:[`item.max_hours`]="{ item }">{{ humanize(item.max_hours) }}</template>
                  </v-data-table>
                </template>
              </v-card-text>
            </v-card>

            <!-- Detalle transiciones -->
            <v-card outlined class="mt-3">
              <v-card-text class="pa-0">
                <v-data-table
                  :headers="headers"
                  :items="visibleTransitions"
                  :search="search"
                  :loading="loading"
                  locale="es-do"
                  :items-per-page="15"
                  :footer-props="{ 'items-per-page-options': [15, 30, 50, -1] }"
                  no-data-text="No se encontraron transiciones con los filtros aplicados"
                >
                  <template v-slot:top>
                    <v-toolbar flat>
                      <span style="font-size: 15px">Transiciones: {{ visibleTransitions.length }}</span>
                      <v-divider class="mx-4" inset vertical></v-divider>
                      <v-text-field
                        v-model="search"
                        append-icon="search"
                        label="Buscar estatus"
                        single-line
                        hide-details
                        outlined
                        dense
                        style="max-width: 360px"
                      ></v-text-field>
                      <v-spacer />
                      <v-btn color="success" outlined small @click="exportCsv" :disabled="!visibleTransitions.length">
                        <v-icon left small>mdi-download</v-icon> CSV
                      </v-btn>
                    </v-toolbar>
                  </template>

                  <template v-slot:[`item.from_name`]="{ item }">
                    <v-chip dark small :color="statusColor(item.from_id)">{{ item.from_name }}</v-chip>
                  </template>
                  <template v-slot:[`item.to_name`]="{ item }">
                    <v-icon small class="mr-1 grey--text">east</v-icon>
                    <v-chip dark small :color="statusColor(item.to_id)">{{ item.to_name }}</v-chip>
                  </template>
                  <template v-slot:[`item.transitions`]="{ item }">{{ formatInt(item.transitions) }}</template>
                  <template v-slot:[`item.avg_hours`]="{ item }"><strong>{{ humanize(item.avg_hours) }}</strong></template>
                  <template v-slot:[`item.median_hours`]="{ item }">{{ humanize(item.median_hours) }}</template>
                  <template v-slot:[`item.min_hours`]="{ item }">{{ humanize(item.min_hours) }}</template>
                  <template v-slot:[`item.max_hours`]="{ item }">{{ humanize(item.max_hours) }}</template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import ReportFilters from "@/components/Shop/ReportFilters.vue";
import { generateShopReportsExcel } from "@/helpers/shopReportsExcel";

export default {
  name: "ShopReports",
  components: { ReportFilters },
  data: () => ({
    loading: false,
    exporting: false,
    search: "",
    approverSearch: "",
    orderBy: "avg_hours",
    reqSeq: 0,
    pathSeq: 0,
    currentFilters: null,
    statusOptions: [],
    pathFrom: 2, // Pendiente
    pathTo: 14, // Cerrada
    pathLoading: false,
    pathResult: null,
    // Orden real del flujo de trabajo (no coincide con el id del estatus).
    // Permite detectar avances vs retrocesos (devoluciones/reaperturas).
    statusRank: {
      1: 0, // Borrador
      2: 1, // Pendiente
      3: 2, // Aprobado (Nivel 0)
      4: 3, // Aprobado (Nivel 1)
      5: 4, // Aprobado (Nivel 2)
      6: 5, // Aprobado (Nivel 3)
      7: 6, // Aprobado (Nivel 4)
      8: 7, // Aprobado (Nivel 5)
      9: 8, // Aprobado (Nivel 6)
      10: 9, // Pendiente de enviar
      15: 10, // Pendiente de impresión
      13: 11, // Pendiente de recepción
      14: 12, // Cerrada
    },
    transitions: [],
    approvers: [],
    summary: {
      total_transitions: 0,
      orders_analyzed: 0,
      closed_orders: 0,
      cancelled_orders: 0,
      rejected_orders: 0,
      avg_cycle_hours: null,
      median_cycle_hours: null,
    },
    headers: [
      { text: "Desde", value: "from_name" },
      { text: "Hacia", value: "to_name" },
      { text: "Transiciones", value: "transitions", align: "end" },
      { text: "Promedio", value: "avg_hours", align: "end" },
      { text: "Mediana", value: "median_hours", align: "end" },
      { text: "Mínimo", value: "min_hours", align: "end" },
      { text: "Máximo", value: "max_hours", align: "end" },
    ],
    approverHeaders: [
      { text: "Aprobador", value: "approver" },
      { text: "Aprobaciones", value: "approvals", align: "end" },
      { text: "Promedio", value: "avg_hours", align: "end" },
      { text: "Mediana", value: "median_hours", align: "end" },
      { text: "Mínimo", value: "min_hours", align: "end" },
      { text: "Máximo", value: "max_hours", align: "end" },
    ],
  }),
  computed: {
    summaryCards() {
      const s = this.summary;
      return [
        {
          label: "Transiciones analizadas",
          value: this.formatInt(s.total_transitions),
          sub: "",
          icon: "mdi-swap-horizontal",
          color: "#1976D2",
          bg: "#E7F0FB",
        },
        {
          label: "Órdenes con movimiento",
          value: this.formatInt(s.orders_analyzed),
          sub: "Total del período",
          icon: "mdi-cart-outline",
          color: "#00838F",
          bg: "#E0F3F5",
        },
        {
          label: "Órdenes cerradas",
          value: this.formatInt(s.closed_orders),
          sub: this.pctOfTotal(s.closed_orders),
          icon: "mdi-check-circle-outline",
          color: "#2E7D32",
          bg: "#E6F2E7",
        },
        {
          label: "Órdenes canceladas",
          value: this.formatInt(s.cancelled_orders),
          sub: this.pctOfTotal(s.cancelled_orders),
          icon: "mdi-cancel",
          color: "#E65100",
          bg: "#FCEEE2",
        },
        {
          label: "Órdenes rechazadas",
          value: this.formatInt(s.rejected_orders),
          sub: this.pctOfTotal(s.rejected_orders),
          icon: "mdi-close-circle-outline",
          color: "#C62828",
          bg: "#FBE9E9",
        },
        {
          label: "Ciclo promedio",
          value: this.humanize(s.avg_cycle_hours),
          sub:
            s.median_cycle_hours != null
              ? `Creación → cerrada · Mediana: ${this.humanize(s.median_cycle_hours)}`
              : "Creación → cerrada",
          icon: "mdi-clock-outline",
          color: "#5E35B1",
          bg: "#EEE9F7",
        },
      ];
    },
    pathCards() {
      const p = this.pathResult || {};
      return [
        { label: "Promedio", value: this.humanize(p.avg_hours), icon: "mdi-clock-outline", color: "#1976D2", bg: "#E7F0FB" },
        { label: "Mediana", value: this.humanize(p.median_hours), icon: "mdi-align-vertical-center", color: "#00838F", bg: "#E0F3F5" },
        { label: "Mínimo", value: this.humanize(p.min_hours), icon: "mdi-speedometer", color: "#2E7D32", bg: "#E6F2E7" },
        { label: "Máximo", value: this.humanize(p.max_hours), icon: "mdi-timer-sand", color: "#E65100", bg: "#FCEEE2" },
      ];
    },
    decoratedTransitions() {
      return this.transitions.map((t) => {
        const rf = this.statusRank[t.from_id];
        const rt = this.statusRank[t.to_id];
        let direction;
        if ([11, 12].includes(t.to_id)) direction = "anulacion";
        else if (rf == null || rt == null) direction = "avance";
        else direction = rt >= rf ? "avance" : "retroceso";
        return { ...t, direction };
      });
    },
    visibleTransitions() {
      // Solo avances: se ocultan retrocesos (devoluciones/reaperturas) y anulaciones.
      return this.decoratedTransitions.filter((t) => t.direction === "avance");
    },
    transitionChartData() {
      const rows = [...this.visibleTransitions];
      rows.sort((a, b) =>
        this.orderBy === "transitions"
          ? b.transitions - a.transitions
          : (b.avg_hours || 0) - (a.avg_hours || 0)
      );
      return rows.slice(0, 15);
    },
    transitionChartSeries() {
      return [
        {
          name: "Promedio",
          data: this.transitionChartData.map((r) => Number(r.avg_hours) || 0),
        },
      ];
    },
    transitionChartOptions() {
      return this.barOptions(
        this.transitionChartData.map((r) => `${r.from_name} → ${r.to_name}`),
        (i) => {
          const row = this.transitionChartData[i];
          return `${this.formatInt(row.transitions)} transiciones`;
        }
      );
    },
    approverChartData() {
      const rows = [...this.approvers];
      rows.sort((a, b) => (b.avg_hours || 0) - (a.avg_hours || 0));
      return rows.slice(0, 15);
    },
    approverChartSeries() {
      return [
        {
          name: "Promedio",
          data: this.approverChartData.map((r) => Number(r.avg_hours) || 0),
        },
      ];
    },
    approverChartOptions() {
      return this.barOptions(
        this.approverChartData.map((r) => r.approver),
        (i) => {
          const row = this.approverChartData[i];
          return `${this.formatInt(row.approvals)} aprobaciones`;
        }
      );
    },
  },
  async created() {
    try {
      const res = await this.api_Get("shopping/order_status");
      this.statusOptions = (await res.json()) || [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[ShopReports] order_status", error);
    }
  },
  methods: {
    onFiltersChanged(filters) {
      if (!filters) return;
      this.currentFilters = filters;
      this.getReport(filters);
      this.getPath();
    },
    statusName(id) {
      const s = this.statusOptions.find((x) => x.id === id);
      return s ? s.name : "";
    },
    businessLabel() {
      const ids = (this.currentFilters && this.currentFilters.business_ids) || [];
      const units = this.$store.state.auth.bussines_units || [];
      if (!ids.length) return "Todas las unidades de negocio";
      const names = ids
        .map((id) => {
          const u = units.find((x) => x.id == id);
          return u ? u.description_long || u.text || `Unidad ${id}` : `Unidad ${id}`;
        });
      return names.join(", ");
    },
    async getPath() {
      if (
        !this.currentFilters ||
        !this.pathFrom ||
        !this.pathTo ||
        this.pathFrom === this.pathTo
      ) {
        this.pathResult = null;
        return;
      }
      const seq = ++this.pathSeq;
      this.pathLoading = true;
      try {
        const f = this.currentFilters;
        const res = await this.api_Post("shopping/reports/status_path_time", {
          business_unit_ids: f.business_ids || [],
          location_ids: f.location_ids || [],
          project_id: f.project_id || 0,
          department_id: f.department_id || 0,
          from_status: this.pathFrom,
          to_status: this.pathTo,
          dateStart: f.dateStart,
          dateEnd: f.dateEnd,
        });
        const data = await res.json();
        if (seq !== this.pathSeq) return;
        if (res.status === 200) {
          this.pathResult = data;
        } else {
          this.pathResult = null;
          this.$toast.error(data.error || "Error al calcular el tiempo entre estatus");
        }
      } catch (error) {
        if (seq === this.pathSeq)
          this.$toast.error("Error al calcular el tiempo entre estatus");
        // eslint-disable-next-line no-console
        console.error("[ShopReports] getPath", error);
      } finally {
        if (seq === this.pathSeq) this.pathLoading = false;
      }
    },
    async getReport(filters) {
      const seq = ++this.reqSeq;
      this.loading = true;
      try {
        const payload = {
          business_unit_ids: filters.business_ids || [],
          location_ids: filters.location_ids || [],
          project_id: filters.project_id || 0,
          department_id: filters.department_id || 0,
          statuses: filters.statuses || [],
          dateStart: filters.dateStart,
          dateEnd: filters.dateEnd,
        };

        const response = await this.api_Post(
          "shopping/reports/status_transition_times",
          payload
        );
        const data = await response.json();

        // Evita que una respuesta vieja sobreescriba una más reciente.
        if (seq !== this.reqSeq) return;

        if (response.status === 200) {
          this.transitions = data.transitions || [];
          this.approvers = data.approvers || [];
          this.summary = data.summary || this.summary;
        } else {
          this.$toast.error(data.error || "Error al generar el reporte");
        }
      } catch (error) {
        if (seq === this.reqSeq) this.$toast.error("Error al generar el reporte");
        // eslint-disable-next-line no-console
        console.error("[ShopReports] getReport", error);
      } finally {
        if (seq === this.reqSeq) this.loading = false;
      }
    },
    barOptions(categories, tooltipExtra) {
      const self = this;
      return {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
        },
        colors: ["#1976D2"],
        dataLabels: {
          enabled: true,
          formatter: (val) => self.humanize(val),
          style: { fontSize: "11px", colors: ["#fff"] },
        },
        legend: { show: false },
        grid: { padding: { left: 10 } },
        xaxis: {
          categories,
          labels: { formatter: (val) => self.humanize(val) },
          title: { text: "Tiempo promedio" },
        },
        yaxis: {
          labels: { maxWidth: 360, style: { fontSize: "12px" } },
        },
        tooltip: {
          y: {
            formatter: (val, opts) =>
              `${self.humanize(val)}  ·  ${tooltipExtra(opts.dataPointIndex)}`,
          },
        },
      };
    },
    chartHeight(count) {
      return Math.max(220, (count || 1) * 34 + 80);
    },
    humanize(hours) {
      if (hours === null || hours === undefined || hours === "") return "—";
      const h = Number(hours);
      if (isNaN(h)) return "—";
      if (h < 1) return `${Math.round(h * 60)} min`;
      if (h < 48) return `${h.toFixed(1)} h`;
      return `${(h / 24).toFixed(1)} días`;
    },
    formatInt(value) {
      const n = Number(value || 0);
      return n.toLocaleString("es-do");
    },
    pctOfTotal(count) {
      const total = Number(this.summary.orders_analyzed) || 0;
      const n = Number(count) || 0;
      if (!total) return "";
      return `${((n / total) * 100).toFixed(1)}% del total`;
    },
    statusColor(statusId) {
      const cfg = this.time_line_orders(statusId);
      return cfg ? cfg.color : "blue-grey";
    },
    exportCsv() {
      const cols = [
        "Desde",
        "Hacia",
        "Transiciones",
        "Promedio (h)",
        "Mediana (h)",
        "Mínimo (h)",
        "Máximo (h)",
      ];
      const lines = [cols.join(",")];
      this.visibleTransitions.forEach((r) => {
        lines.push(
          [
            `"${r.from_name}"`,
            `"${r.to_name}"`,
            r.transitions,
            r.avg_hours,
            r.median_hours,
            r.min_hours,
            r.max_hours,
          ].join(",")
        );
      });
      const blob = new Blob(["﻿" + lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "reporte_transiciones_compras.csv";
      link.click();
      URL.revokeObjectURL(link.href);
    },
    async chartDataURI(ref, count) {
      const comp = this.$refs[ref];
      if (!comp || typeof comp.dataURI !== "function") return null;
      try {
        const { imgURI } = await comp.dataURI();
        return { uri: imgURI, height: this.chartHeight(count) };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[ShopReports] chartDataURI", ref, e);
        return null;
      }
    },
    async exportExcel() {
      if (!this.currentFilters) return;
      this.exporting = true;
      try {
        const [transitionImg, approverImg] = await Promise.all([
          this.visibleTransitions.length
            ? this.chartDataURI("transitionChart", this.transitionChartData.length)
            : null,
          this.approvers.length
            ? this.chartDataURI("approverChart", this.approverChartData.length)
            : null,
        ]);

        await generateShopReportsExcel({
          summary: this.summary,
          path: this.pathResult,
          pathFromName: this.statusName(this.pathFrom),
          pathToName: this.statusName(this.pathTo),
          transitions: this.visibleTransitions,
          rawTransitions: this.decoratedTransitions,
          approvers: this.approvers,
          meta: {
            businessName: this.businessLabel(),
            dateStart: this.currentFilters.dateStart,
            dateEnd: this.currentFilters.dateEnd,
          },
          charts: {
            transition: transitionImg ? transitionImg.uri : null,
            transitionHeight: transitionImg ? transitionImg.height : null,
            approver: approverImg ? approverImg.uri : null,
            approverHeight: approverImg ? approverImg.height : null,
          },
        });
      } catch (error) {
        this.$toast.error("Error al exportar el Excel");
        // eslint-disable-next-line no-console
        console.error("[ShopReports] exportExcel", error);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.metric-card {
  border-radius: 12px;
  border-color: #e3e8ef !important;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.metric-card:hover {
  box-shadow: 0 4px 14px rgba(16, 42, 83, 0.08);
  transform: translateY(-1px);
}
.metric-label {
  font-size: 11px;
  font-weight: 600;
  color: #8a98ab;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.3;
}
.metric-value {
  font-size: 26px;
  font-weight: 600;
  color: #0e1b2c;
  line-height: 1.2;
}
.metric-sub {
  font-size: 11px;
  color: #8a98ab;
  min-height: 15px;
}
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #4a5568;
}
.path-summary {
  font-size: 15px;
  color: #0e1b2c;
}
</style>
